import Alert from "@/components/alert/Alert";
import ToggleInput from "@/components/toggle/Toggle";
import { s3UploadCampaignImage } from "@/helpers/FileFunctions";
import { useAppSelector } from "@/hooks/hook";
import { POST, PUT } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { addTimestampToFile } from "@thdinc/hpf-shared";
import React, { useEffect, useRef, useState } from "react";
import { FaCloudUploadAlt, FaLink } from "react-icons/fa";
import { IoMdArrowBack } from "react-icons/io";
import { Campaign } from "../interface";

interface AddCampaignModalProps {
    editingCampaign?: Campaign | null;
    onBack: () => void;
    onSuccess: () => void;
}

const inputClass =
    "w-full px-4 py-3 bg-p-gray rounded-xl focus:outline-none focus:ring-1 focus:ring-primary-red text-sm text-gray-700 placeholder:text-gray-400";
const labelClass = "text-xs font-semibold text-gray-600 mb-1.5 block";

const emptyForm = {
    name: "",
    url: "",
    image_url: "",
    points: "",
    isActive: true,
};

const AddCampaignModal: React.FC<AddCampaignModalProps> = ({ editingCampaign, onBack, onSuccess }) => {
    const { refreshToken } = useAppSelector((store) => store.user);
    const { campaignsApi } = API_ROUTES;
    const isEditing = Boolean(editingCampaign);

    const [form, setForm] = useState(emptyForm);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Prefill the form when editing an existing campaign, reset when adding a new one
    useEffect(() => {
        if (editingCampaign) {
            setForm({
                name: editingCampaign.name,
                url: editingCampaign.url,
                image_url: editingCampaign.image_url,
                points: String(editingCampaign.points ?? 0),
                isActive: editingCampaign.is_active,
            });
        } else {
            setForm(emptyForm);
        }
        setImageFile(null);
        setImagePreview(null);
    }, [editingCampaign]);

    // Only used for a local preview of a newly picked file — release it once replaced/unmounted
    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const handleImageSelect = (file: File) => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) return Alert({ type: "error", message: "Name is required" });
        if (form.name.trim().length > 150) return Alert({ type: "error", message: "Name must be at most 150 characters" });
        if (!form.url.trim()) return Alert({ type: "error", message: "Link is required" });
        if (!imageFile && !form.image_url.trim()) return Alert({ type: "error", message: "Please upload an image" });
        const pointsValue = Number(form.points);
        if (!form.points.trim() || !Number.isInteger(pointsValue) || pointsValue <= 0) {
            return Alert({ type: "error", message: "Points must be a whole number greater than 0" });
        }

        const normalizedUrl = /^https?:\/\//i.test(form.url.trim()) ? form.url.trim() : `https://${form.url.trim()}`;

        try {
            setIsSubmitting(true);

            let imageUrl = form.image_url;
            if (imageFile) {
                const uploadRes = await s3UploadCampaignImage(addTimestampToFile(imageFile), refreshToken as string);
                if (!uploadRes?.data?.success || !uploadRes?.data?.url) {
                    throw new Error(uploadRes?.data?.message || "Image upload failed");
                }
                imageUrl = uploadRes.data.url;
            }

            const payload = {
                name: form.name.trim(),
                url: normalizedUrl,
                image_url: imageUrl,
                points: pointsValue,
                is_active: form.isActive,
            };
            const res = isEditing
                ? await PUT(`${campaignsApi}/${editingCampaign?.id}`, payload)
                : await POST(campaignsApi, payload);
            Alert({ message: res?.data?.message ?? (isEditing ? "Campaign updated" : "Campaign added") });
            setForm(emptyForm);
            setImageFile(null);
            setImagePreview(null);
            onSuccess();
        } catch (error: any) {
            Alert({
                type: "error",
                message:
                    error?.response?.data?.message ?? error?.message ?? `Unable to ${isEditing ? "update" : "add"} campaign`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const previewSrc = imagePreview ?? form.image_url;

    return (
        <div className="flex flex-col gap-4">
            <div
                onClick={() => fileInputRef.current?.click()}
                className="rounded-xl border border-dashed border-gray-300 bg-p-gray px-4 py-8 flex flex-col items-center justify-center text-center cursor-pointer transition-colors"
            >
                {previewSrc ? (
                    <img
                        src={previewSrc}
                        alt="Campaign preview"
                        className="w-20 h-20 rounded-md object-cover shrink-0 mb-2"
                    />
                ) : (
                    <span className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm mb-2">
                        <FaCloudUploadAlt className="text-gray-500" size={16} />
                    </span>
                )}
                <p className="text-sm font-medium text-gray-700">
                    {previewSrc ? "Click to replace image" : "Click to upload an image"}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                    {imageFile ? "Image will be uploaded when you save" : "PNG or JPG"}
                </p>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageSelect(file);
                    }}
                />
            </div>

            <div>
                <p className={labelClass}>Name</p>
                <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={150}
                    placeholder="e.g. Summer Rewards Boost"
                    className={inputClass}
                />
            </div>

            <div>
                <p className={labelClass}>Link</p>
                <div className="relative">
                    <FaLink className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                    <input
                        type="text"
                        value={form.url}
                        onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                        placeholder="Where this campaign should link to"
                        className={`${inputClass} pl-10`}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <p className={labelClass}>Points</p>
                    <input
                        type="number"
                        min={1}
                        step={1}
                        value={form.points}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            setForm((f) => ({ ...f, points: value }));
                        }}
                        onKeyDown={(e) => {
                            if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
                        }}
                        placeholder="0"
                        className={inputClass}
                    />
                </div>
                <div>
                    <p className={labelClass}>Status</p>
                    <div className="flex items-center gap-3 bg-p-gray rounded-xl px-4 py-3">
                        <ToggleInput
                            checked={form.isActive}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setForm((f) => ({ ...f, isActive: e.target.checked }))
                            }
                        />
                        <span className={`text-sm font-medium ${form.isActive ? "text-emerald-600" : "text-gray-400"}`}>
                            {form.isActive ? "Active" : "Inactive"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium text-gray-600 border border-gray-200 disabled:opacity-50"
                >
                    <IoMdArrowBack /> Back
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-5 py-2 rounded-full text-sm font-medium bg-primary-red hover:bg-primary-red-button-hover text-white whitespace-nowrap disabled:opacity-60"
                >
                    {isSubmitting ? "Saving..." : isEditing ? "Save changes" : "+ Add Campaign"}
                </button>
            </div>
        </div>
    );
};

export default AddCampaignModal;
